import { BaseComponent } from '../base-component'
import {
  $el,
  $list,
  $elParent,
  transitionToPromise,
  getTargets,
  isNotIframe,
  whenDefined,
} from '../../utils/utils'
import { isEscKey } from '../../utils/key'
import { getTemplateFirstChild } from '../../utils/dom'
import { Nullable } from '../../types/common'
import { BodyElement } from '../body-element/body-element'
import { CloseCursor } from 'src/js/components/close-cursor/close-cursor'
import { LazyVideo } from '../body-element/lazy-video'

const BODY_ELEMENT_SELECTOR = 'body-element'

export class SidebarComponent extends BaseComponent {
  template: Nullable<HTMLTemplateElement>
  element: HTMLElement
  touchStartX: number
  touchStartY: number
  touchEndX: number
  touchEndY: number
  isOpen: boolean

  constructor() {
    super()

    if (this.hasAttribute('is-open-on-init')) {
      this.element = $el('[data-sidebar-body]', this)
    } else {
      const template = $el(`[data-sidebar-template="${this.id}"]`, this)
      this.element = getTemplateFirstChild(template)
    }

    this.isOpen = this.classList.contains('is-opened')
  }

  mountComponent(): void {
    super.mountComponent()

    const withOutsideClick = this.hasAttribute('data-with-close-on-outside')

    this.addListener(this, 'touchstart', this.handleTouchStart)
    this.addListener(this, 'touchmove', this.handleTouchMove)
    this.addListener(this, 'touchend', this.handleTouchEnd)

    if (withOutsideClick) {
      this.addListener(window, 'click', this.handleOutsideClick)
    }

    if (this.isEditor) {
      if (!this.isSection) {
        this.hide(true)
      }

      this.editor.on('SECTION_SELECT', this.handleSectionSelect)
      this.editor.on('SECTION_DESELECT', this.handleSectionDeselect)
    }
  }

  handleSidebarFocusout = event => {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    const focusTarget = event.relatedTarget
    const isLastOpenedWindow = bodyElement.isLastOpenedWindow(this.id)
    const isfocusTargetOut = !focusTarget || !this.contains(focusTarget)

    if (isfocusTargetOut && isLastOpenedWindow && bodyElement.isUsingKeyboard) {
      const { firstTarget } = getTargets(this.element)

      firstTarget.focus()
    }
  }

  handleSectionSelect = ({ detail: { sectionId } }): void => {
    if (this.isSection && this.dataset.sectionId === sectionId) {
      this.open()
    }
  }

  handleSectionDeselect = ({ detail: { sectionId } }): void => {
    if (this.isSection && this.dataset.sectionId === sectionId) {
      this.hide()
    }
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const isOverlay = $elParent(BODY_ELEMENT_SELECTOR, event.target)

    if (isOverlay && this.isOpen) {
      this.hide()
    }
  }

  handleTouchStart = (event: TouchEvent): void => {
    this.touchStartX = event.targetTouches[0].clientX
    this.touchStartY = event.targetTouches[0].clientY
  }

  handleTouchMove = (event: TouchEvent): void => {
    this.touchEndX = event.targetTouches[0].clientX
    this.touchEndY = event.targetTouches[0].clientY
  }

  handleTouchEnd = (event): void => {
    const X_DIRECTION_THRESHOLD = this.offsetWidth / 2
    const Y_DIRECTION_THRESHOLD = 48

    const distanceX = Math.abs(this.touchStartX - this.touchEndX)
    const distanceY = Math.abs(this.touchStartY - this.touchEndY)
    const isCarousel = $elParent('[data-carousel-viewport]', event.target)

    if (isCarousel) {
      this.cleanTouchPoints()
      return
    }

    if (this.touchEndX === 0) {
      this.cleanTouchPoints()
      return
    }

    if (distanceY > Y_DIRECTION_THRESHOLD) {
      this.cleanTouchPoints()
      return
    }

    const isTrigger =
      this.dataset.openDirection === 'right'
        ? this.touchStartX < this.touchEndX
        : this.touchStartX > this.touchEndX

    if (this.isOpen && distanceX > X_DIRECTION_THRESHOLD && isTrigger) {
      this.hide()
    }

    this.cleanTouchPoints()
  }

  handleKeydown = (event: KeyboardEvent): void => {
    const withCloseOnEscape = this.hasAttribute('data-with-close-on-escape')

    if (withCloseOnEscape && isEscKey(event)) {
      event.preventDefault()
      this.hide()
    }
  }

  cleanTouchPoints(): void {
    this.touchStartX = 0
    this.touchEndX = 0
    this.touchStartY = 0
    this.touchEndY = 0
  }

  async open(event?: any): Promise<void> {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    whenDefined('body-element').then(() => {
      bodyElement.addDialogWindow(this.id)
    })

    const closeCursor: CloseCursor = $el('close-cursor')

    this.mount()

    this.setOpenState(true)
    await transitionToPromise(this)

    const videos: any = $list('lazy-video, video', this)

    videos.forEach(video => {
      video.play ? video.play() : video.initVideo()
    })

    this.initFocus()

    this.addListener(window, 'keydown', this.handleKeydown)
    this.addListener(this, 'focusout', this.handleSidebarFocusout)

    whenDefined('close-cursor').then(() => {
      closeCursor?.toggle(event)
    })
  }

  async hide(isInstant?: boolean): Promise<void> {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    whenDefined('body-element').then(() => {
      bodyElement.removeDialogWindow(this.id)
    })

    this.removeListener(window, 'keydown', this.handleKeydown)
    this.removeListener(this, 'focusout', this.handleSidebarFocusout)

    const closeCursor: CloseCursor = $el('close-cursor')
    whenDefined('close-cursor').then(() => {
      closeCursor?.toggle()
    })

    this.setOpenState(false)

    if (isInstant) {
      this.unmount()
    } else {
      await transitionToPromise(this)
      this.unmount()
    }
  }

  initFocus(): void {
    if (isNotIframe()) {
      const { firstTarget } = getTargets(this)

      firstTarget?.focus()
    }
  }

  async toggle(event): Promise<void> {
    if (this.isOpen) {
      this.hide()
    } else {
      this.open(event)
    }
  }

  mount(): void {
    this.replaceChildren(this.element)
  }

  unmount(): void {
    this.replaceChildren()
  }

  updateTemplate(template: HTMLTemplateElement): void {
    this.element = getTemplateFirstChild(template)
    this.replaceChildren(this.element)
  }

  setOpenState(isOpen: boolean) {
    this.isOpen = isOpen

    const withOverlay = this.hasAttribute('data-with-overlay')
    const buttons = $list(`[data-sidebar-button][data-sidebar-id="${this.id}"]`)

    this.setVisible(isOpen)

    if (withOverlay) {
      const isImportant = this.hasAttribute('data-overlay-important')
      const hasCustomOpacity = this.hasAttribute('data-overlay-opacity')
      const computedStyles = getComputedStyle(this)
      const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
      let styles = {}

      if (isImportant) {
        styles = { ...styles, zIndex: parseFloat(computedStyles['zIndex']) - 1 }
      }

      if (hasCustomOpacity) {
        styles = {
          ...styles,
          '--gsc-overlay-opacity': +this.getAttribute('data-overlay-opacity'),
        }
      }

      whenDefined('body-element').then(() => {
        if (isOpen) {
          bodyElement.showOverlay(this.id, styles)
        } else {
          bodyElement.hideOverlay(this.id)
        }
      })
    }

    buttons.forEach(button => {
      button.classList.toggle('is-opened', isOpen)
    })
  }

  setVisible(isVisible: boolean): void {
    this.classList.toggle('is-opened', isVisible)
  }

  get isSection(): boolean {
    return this.hasAttribute('data-sidebar-section')
  }
}
